import type {
  BankTransaction,
  Facility,
  FacilityCategoryData,
  AccountImportStatus,
  CrossCheckingKpi,
  CrossCheckingTransaction,
} from '@/types/banking'

/**
 * Mock facilities data (17 facilities from design).
 *
 * `sage_location_id` values are populated with realistic `LOC-<CODE>`
 * tokens so the Edit Facility dialog (DCP-154) doesn't trip its own
 * Zod `.min(1)` validation when a developer opens the dialog against
 * MSW-mocked data. Real tenants get values from the actual Sage Intacct
 * tenant; these mocks just need to satisfy the same shape contract.
 */
export const mockFacilities: Facility[] = [
  { id: 'fc', name: 'First Colony', code: 'FC', sage_location_id: 'LOC-FC', is_active: true, pack_id: null },
  { id: 'ec', name: 'El Campo', code: 'EC', sage_location_id: 'LOC-EC', is_active: true, pack_id: null },
  { id: 'bc', name: 'Bay City', code: 'BC', sage_location_id: 'LOC-BC', is_active: true, pack_id: null },
  { id: 'bv', name: 'Brathesview', code: 'BV', sage_location_id: 'LOC-BV', is_active: true, pack_id: null },
  { id: 'dw', name: 'DeWitt', code: 'DW', sage_location_id: 'LOC-DW', is_active: true, pack_id: null },
  { id: 'pn', name: 'Pines', code: 'PN', sage_location_id: 'LOC-PN', is_active: true, pack_id: null },
  { id: 'sb', name: 'Schulenburg', code: 'SB', sage_location_id: 'LOC-SB', is_active: true, pack_id: null },
  { id: 'wh', name: 'Wharton', code: 'WH', sage_location_id: 'LOC-WH', is_active: true, pack_id: null },
  { id: 'wi', name: 'Woodwind Isles', code: 'WI', sage_location_id: 'LOC-WI', is_active: true, pack_id: null },
  { id: 'kt', name: 'Katy', code: 'KT', sage_location_id: 'LOC-KT', is_active: true, pack_id: null },
  { id: 'sw', name: 'Sweeney', code: 'SW', sage_location_id: 'LOC-SW', is_active: true, pack_id: null },
  { id: 'fm', name: 'Faith Memorial', code: 'FM', sage_location_id: 'LOC-FM', is_active: true, pack_id: null },
  { id: 'kz', name: 'Kourtze', code: 'KZ', sage_location_id: 'LOC-KZ', is_active: true, pack_id: null },
  { id: 'wb', name: 'Westbury', code: 'WB', sage_location_id: 'LOC-WB', is_active: true, pack_id: null },
  { id: 'nz', name: 'Nazareth', code: 'NZ', sage_location_id: 'LOC-NZ', is_active: true, pack_id: null },
  { id: 'nw', name: 'North West', code: 'NW', sage_location_id: 'LOC-NW', is_active: true, pack_id: null },
  { id: 'ph', name: 'Paradigm HC', code: 'PH', sage_location_id: 'LOC-PH', is_active: true, pack_id: null },
  // Default every mock facility to source='manual' (DCP-262) and no Legal
  // Name yet. The literals above omit both so the array stays readable; the
  // map stamps the defaults so the whole thing still satisfies Facility[].
].map((f) => ({ ...f, legal_name: null, source: 'manual' as const }))

/**
 * Mock bank transactions (sample data from design)
 */
export const mockTransactions: BankTransaction[] = [
  {
    id: 'txn_1',
    facility: 'First Colony',
    date: '2025-06-04',
    accountNumber: '2Q2619',
    accountName: 'FC_CIBC619',
    category: 'INCOME',
    categoryId: null,
    amount: 91700,
    description: 'FIRST COLONY NON CORP PAY',
    memo: '',
    apiStatus: 'synced',
    pending: false,
  },
  {
    id: 'txn_2',
    facility: 'First Colony',
    date: '2025-06-04',
    accountNumber: '2Q2619',
    accountName: 'FC_CIBC619',
    category: 'LOAN_PAYMENTS',
    categoryId: null,
    amount: -145000,
    description: 'LOC Paydown',
    memo: 'LOC Paydown',
    apiStatus: 'synced',
    pending: false,
  },
  {
    id: 'txn_3',
    facility: 'DeWitt',
    date: '2025-06-04',
    accountNumber: '123144',
    accountName: 'CIBC1444',
    category: 'GENERAL_MERCHANDISE',
    categoryId: null,
    amount: -1850,
    description: 'MEGADAT/AHEALTHSY PURCHASE - 2411196 ARADON SHOP',
    memo: '149',
    apiStatus: 'pending',
    pending: false,
  },
  {
    id: 'txn_4',
    facility: 'DeWitt',
    date: '2025-06-04',
    accountNumber: '123144',
    accountName: 'CIBC1444',
    category: 'GENERAL_MERCHANDISE',
    categoryId: null,
    amount: -1408,
    description: 'CHECK PAID',
    memo: '',
    apiStatus: 'synced',
    pending: false,
  },
  {
    id: 'txn_5',
    facility: 'First Colony',
    date: '2025-06-04',
    accountNumber: '2Q2619',
    accountName: 'FC_CIBC619',
    category: 'INCOME',
    categoryId: null,
    amount: 91700,
    description: 'FIRST COLONY NON CORP PAY',
    memo: '',
    apiStatus: 'synced',
    pending: false,
  },
  {
    id: 'txn_6',
    facility: 'First Colony',
    date: '2025-06-04',
    accountNumber: '2Q2619',
    accountName: 'FC_CIBC619',
    category: 'LOAN_PAYMENTS',
    categoryId: null,
    amount: -145000,
    description: 'LOC Paydown',
    memo: 'LOC Paydown',
    apiStatus: 'pending',
    pending: false,
  },
]

/**
 * Mock facility category data for horizontal bar chart
 */
export const mockFacilityCategoryData: FacilityCategoryData[] = [
  { facility: 'First Colony', INCOME: 92, GENERAL_MERCHANDISE: 15, LOAN_PAYMENTS: 45, FOOD_AND_DRINK: 25, ENTERTAINMENT: 10, OTHER: 8 },
  { facility: 'El Campo', INCOME: 78, GENERAL_MERCHANDISE: 22, LOAN_PAYMENTS: 35, FOOD_AND_DRINK: 18, ENTERTAINMENT: 12, OTHER: 5 },
  { facility: 'Bay City', INCOME: 65, GENERAL_MERCHANDISE: 18, LOAN_PAYMENTS: 28, FOOD_AND_DRINK: 15, ENTERTAINMENT: 8, OTHER: 6 },
  { facility: 'Brathesview', INCOME: 55, GENERAL_MERCHANDISE: 25, LOAN_PAYMENTS: 32, FOOD_AND_DRINK: 20, ENTERTAINMENT: 15, OTHER: 3 },
  { facility: 'DeWitt', INCOME: 48, GENERAL_MERCHANDISE: 30, LOAN_PAYMENTS: 25, FOOD_AND_DRINK: 12, ENTERTAINMENT: 5, OTHER: 10 },
  { facility: 'Pines', INCOME: 72, GENERAL_MERCHANDISE: 20, LOAN_PAYMENTS: 38, FOOD_AND_DRINK: 22, ENTERTAINMENT: 11, OTHER: 7 },
  { facility: 'Schulenburg', INCOME: 60, GENERAL_MERCHANDISE: 15, LOAN_PAYMENTS: 30, FOOD_AND_DRINK: 18, ENTERTAINMENT: 9, OTHER: 8 },
  { facility: 'Wharton', INCOME: 85, GENERAL_MERCHANDISE: 28, LOAN_PAYMENTS: 42, FOOD_AND_DRINK: 25, ENTERTAINMENT: 14, OTHER: 6 },
  { facility: 'Woodwind Isles', INCOME: 45, GENERAL_MERCHANDISE: 12, LOAN_PAYMENTS: 22, FOOD_AND_DRINK: 10, ENTERTAINMENT: 6, OTHER: 5 },
  { facility: 'Katy', INCOME: 95, GENERAL_MERCHANDISE: 35, LOAN_PAYMENTS: 50, FOOD_AND_DRINK: 30, ENTERTAINMENT: 18, OTHER: 12 },
  { facility: 'Sweeney', INCOME: 38, GENERAL_MERCHANDISE: 10, LOAN_PAYMENTS: 18, FOOD_AND_DRINK: 8, ENTERTAINMENT: 4, OTHER: 2 },
  { facility: 'Faith Memorial', INCOME: 52, GENERAL_MERCHANDISE: 18, LOAN_PAYMENTS: 28, FOOD_AND_DRINK: 15, ENTERTAINMENT: 8, OTHER: 9 },
  { facility: 'Kourtze', INCOME: 42, GENERAL_MERCHANDISE: 14, LOAN_PAYMENTS: 20, FOOD_AND_DRINK: 12, ENTERTAINMENT: 6, OTHER: 4 },
  { facility: 'Westbury', INCOME: 68, GENERAL_MERCHANDISE: 24, LOAN_PAYMENTS: 35, FOOD_AND_DRINK: 20, ENTERTAINMENT: 12, OTHER: 7 },
]

/**
 * Mock account import status data
 */
export const mockAccountImportStatus: AccountImportStatus[] = [
  {
    id: 'ais_1',
    facility: 'First Colony',
    bank: 'CIBC Bank',
    accountName: 'FC_CIBC619',
    accountNumber: '2926819',
    numberOfRows: 15,
    apiUpdateStatus: 'success',
    apiUpdateMessage: 'bank transactions successfully imported',
    lastApiUpdateTimestamp: '2025-08-04T05:00:14Z',
    lastUpdateSource: 'API',
    lastUpdateTimestamp: '2025-08-04T09:00:14Z',
  },
  {
    id: 'ais_2',
    facility: 'Key',
    bank: 'Bank Encore',
    accountName: 'BE_K_7818',
    accountNumber: '720007818',
    numberOfRows: null,
    apiUpdateStatus: 'failed',
    apiUpdateMessage: 'failed while API update. Resync manually',
    lastApiUpdateTimestamp: '2025-08-04T09:00:00Z',
    lastUpdateSource: 'File Import',
    lastUpdateTimestamp: '2025-08-04T09:19:12Z',
  },
  {
    id: 'ais_3',
    facility: 'Westbury',
    bank: 'Bank Financial',
    accountName: 'WP_BE_7876',
    accountNumber: '720007876',
    numberOfRows: 12,
    apiUpdateStatus: 'success',
    apiUpdateMessage: 'bank transactions successfully imported',
    lastApiUpdateTimestamp: '2025-08-04T08:58:56Z',
    lastUpdateSource: 'API',
    lastUpdateTimestamp: '2025-08-04T08:58:56Z',
  },
  {
    id: 'ais_4',
    facility: 'Wyoming',
    bank: '',
    accountName: 'WL_BF_7892',
    accountNumber: '720007892',
    numberOfRows: 7,
    apiUpdateStatus: 'success',
    apiUpdateMessage: 'bank transactions successfully imported',
    lastApiUpdateTimestamp: '2025-08-04T08:58:51Z',
    lastUpdateSource: 'API',
    lastUpdateTimestamp: '2025-08-04T08:58:51Z',
  },
]
/**
 * Mock KPI data for cross-checking page (from screenshot)
 */
export const mockCrossCheckingKpi: CrossCheckingKpi = {
  bankBalance: 50000,
  // Negative by the useBankingKpi convention; Book = Bank + Outstanding.
  outstandingChecks: -1000,
  bookBalance: 49000,
  intacctBalance: 50800,
  // Book − Intacct.
  bookVsIntacct: -1800,
}

/**
 * Mock cross-checking transactions (from screenshot)
 */
export const mockCrossCheckingTransactions: CrossCheckingTransaction[] = [
  {
    id: 'cc_txn_1',
    facility: 'First Colony',
    date: '2025-06-04',
    accountNumber: '2926819',
    accountName: 'FC-CIBC2619',
    bank: 'CIBC Bank',
    category: 'INCOME',
    categoryId: null,
    amount: 93700,
    description: 'FIRST COLONY NON CDBP PAY',
    memo: '',
    apiStatus: 'synced',
    pending: false,
    transactionNumber: '****',
    transactionStatus: 'DEPOSIT',
    paymentInfo: '****',
    notes: '',
    recordInSageStatus: 'synced',
  },
  {
    id: 'cc_txn_2',
    facility: 'First Colony',
    date: '2025-06-04',
    accountNumber: '2926819',
    accountName: 'FC-CIBC2619',
    bank: 'CIBC Bank',
    category: 'LOAN_PAYMENTS',
    categoryId: null,
    amount: -145000,
    description: 'LOC Paydown',
    memo: 'LOC Paydown',
    apiStatus: 'synced',
    pending: false,
    transactionNumber: '****',
    transactionStatus: 'LINE OF CREDIT',
    paymentInfo: '****',
    notes: '',
    recordInSageStatus: 'synced',
  },
  {
    id: 'cc_txn_3',
    facility: 'DeWitt',
    date: '2025-06-04',
    accountNumber: '123144',
    accountName: 'CIBC1444',
    bank: 'BIH Bank',
    category: 'GENERAL_MERCHANDISE',
    categoryId: null,
    amount: -1650,
    description: 'MEGADATA-A-HGY X SUPPLY',
    memo: '149',
    apiStatus: 'pending',
    pending: false,
    transactionNumber: '****',
    transactionStatus: 'ACCTS PAYABLE',
    paymentInfo: '****',
    notes: '',
    recordInSageStatus: 'pending',
  },
]

